/**
 * Toasts: one at a time, five seconds, dismissible (DESIGN.md § Components
 * `toast`). A plain external store (`useSyncExternalStore`'s shape) rather
 * than a React context/provider tree: `showToast(message)` is meant to be
 * callable from any mutation's success handler, wherever it lives, without
 * threading a context value down to it — the same reasoning
 * `lib/serial-queue.ts` and `lib/loading-phase.ts` already establish for
 * this codebase's other non-server UI state (`ARCHITECTURE-SPINE.md`:
 * "Frontend state: Server state via TanStack Query... URL owns
 * filter/sort/saved-view state" — a toast is neither, and needs its own
 * small, deliberately un-server-state mechanism).
 *
 * A second toast fired while one is showing queues rather than replaces or
 * drops it — both are real confirmations the account holder asked for, and
 * queueing is what keeps them all eventually seen without ever rendering
 * two at once (VC-2.6-10).
 */
export interface ToastState {
  id: number;
  message: string;
}

type Listener = () => void;

export const TOAST_DURATION_MS = 5000;

export class ToastQueue {
  private pending: ToastState[] = [];
  private current: ToastState | null = null;
  private readonly listeners = new Set<Listener>();
  private nextId = 1;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly durationMs: number;

  constructor(durationMs: number = TOAST_DURATION_MS) {
    this.durationMs = durationMs;
  }

  show(message: string): void {
    const toast: ToastState = { id: this.nextId, message };
    this.nextId += 1;
    if (this.current === null) {
      this.current = toast;
      this.scheduleAutoDismiss();
    } else {
      this.pending.push(toast);
    }
    this.emit();
  }

  /** Dismisses the toast on screen right now, early or on its own timer,
   * and immediately shows the next queued one, if any — never two at
   * once. */
  dismiss(): void {
    this.clearTimer();
    this.current = this.pending.shift() ?? null;
    if (this.current !== null) {
      this.scheduleAutoDismiss();
    }
    this.emit();
  }

  getCurrent = (): ToastState | null => this.current;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private scheduleAutoDismiss(): void {
    this.clearTimer();
    this.timer = setTimeout(() => this.dismiss(), this.durationMs);
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private emit(): void {
    this.listeners.forEach((listener) => listener());
  }
}

/** The one queue every real toast in the app shares. */
export const appToastQueue = new ToastQueue();

/** Shows a plain-language confirmation — never an error (DESIGN.md:
 * "errors do not use toasts — they render next to the thing that
 * failed"). */
export function showToast(message: string, queue: ToastQueue = appToastQueue): void {
  queue.show(message);
}
